import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PasswordInputProps {
  id: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  showStrengthMeter?: boolean;
  onStrengthChange?: (strength: string) => void;
}

export function PasswordInput({
  id,
  label,
  value,
  onChange,
  placeholder = "Digite uma senha forte",
  required = false,
  className,
  showStrengthMeter = false,
  onStrengthChange
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Calcular força da senha se necessário
    if (onStrengthChange) {
      const strength = calculatePasswordStrength(newValue);
      onStrengthChange(strength);
    }
  };

  const calculatePasswordStrength = (password: string): string => {
    if (!password) return '';
    
    let score = 0;
    
    // Comprimento
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    
    // Complexidade
    if (/[A-Z]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
    
    if (score <= 2) return 'Fraca';
    if (score <= 4) return 'Média';
    if (score <= 5) return 'Forte';
    return 'Muito Forte';
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
      )}
      
      <div className="relative">
        <Input
          id={id}
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={handlePasswordChange}
          placeholder={placeholder}
          required={required}
          className="pr-10"
        />
        
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
          title={showPassword ? "Ocultar senha" : "Mostrar senha"}
          aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
      </div>
      
      {showStrengthMeter && value && (
        <div className="text-xs text-gray-600">
          <span className="font-medium">Força: </span>
          <span className={cn(
            "font-semibold",
            calculatePasswordStrength(value) === 'Fraca' && "text-red-600",
            calculatePasswordStrength(value) === 'Média' && "text-yellow-600",
            calculatePasswordStrength(value) === 'Forte' && "text-blue-600",
            calculatePasswordStrength(value) === 'Muito Forte' && "text-green-600"
          )}>
            {calculatePasswordStrength(value)}
          </span>
        </div>
      )}
    </div>
  );
}

// Componente específico para formulários de usuário
interface UserPasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  showStrengthMeter?: boolean;
  className?: string;
}

export function UserPasswordInput({ 
  value, 
  onChange, 
  showStrengthMeter = true,
  className 
}: UserPasswordInputProps) {
  return (
    <PasswordInput
      id="password"
      label="Senha"
      value={value}
      onChange={onChange}
      placeholder="Digite uma senha forte"
      required
      showStrengthMeter={showStrengthMeter}
      className={className}
    />
  );
} 